import * as React from 'react';
import { RouteComponentProps, withRouter } from "react-router-dom";
import { css, DefaultButton } from 'office-ui-fabric-react';
import * as analytics from './analytics';
import * as microsoftTeams from '@microsoft/teams-js'
import './LoginPage.css'
import { inIframe } from './components/utils/common';
import Spinner from './components/common/Spinner';
import { AuthProvider, AuthProvidersMap } from './entities/common';
import { SecuritySettings } from './store/Tenant';

export type LoginProps = {
    region: string | null,
    authProviders: AuthProvider[],
    security?: SecuritySettings;
    inviteAuthProvider?: AuthProvider,
}
type State = {
    redirectUri: string | null,
    isInIframe?: boolean,
    signout: boolean,
    lastAuthProvider: AuthProvider | null,
    allowedAuthOptions: AuthProvider[]
}

export enum SignUpParams {
    Plan = 'plan',
    Type = 'type',
    ReferralCode = 'referralCode',
    CompanyName = 'companyName',
    PersonName = "personName",
    PersonPhone = "personPhone",
    PersonEmail = "personEmail",
}

const REDIRECTURI = "redirectUri";
const AUTH_PROVIDER = 'AUTH_PROVIDER';

const MAX_COUNT_TO_DISPLAY_LIKE_COLUMN = 2;
class LoginPage extends React.Component<LoginProps & RouteComponentProps<{}>, State> {
    constructor(props: LoginProps & RouteComponentProps<{}>) {
        super(props);
        this.state = this._buildState(this.props);
    }

    private _buildState(props: LoginProps & RouteComponentProps<{}>): State {
        const { authProviders, inviteAuthProvider } = props;
        const { allowedAuthProviders } = props.security!;
        let allowedAuthOptions = allowedAuthProviders && authProviders.filter(_ => allowedAuthProviders.includes(_));
        if (inviteAuthProvider && allowedAuthOptions?.length) {
            allowedAuthOptions = authProviders.filter(_ => inviteAuthProvider === _)
        }
        if (!allowedAuthOptions?.length) {
            allowedAuthOptions = this.props.authProviders.filter(_ => _ !== AuthProvider.okta);
        }

        const lastAuthProviderValue = this.getCookie(AUTH_PROVIDER) as AuthProvider;
        const lastAuthProvider = lastAuthProviderValue && allowedAuthOptions.includes(lastAuthProviderValue) ? lastAuthProviderValue : null;

        const params = new URLSearchParams(props.location.search);
        return {
            redirectUri: params.get(REDIRECTURI),
            isInIframe: inIframe(),
            signout: params.has("signout"),
            allowedAuthOptions,
            lastAuthProvider
        };
    }

    componentWillMount() {
        //keep requested query params in cookies and pick it up during tenant registration
        moveToCookie(Object.values(SignUpParams), window.location.search)
        if (this.state.lastAuthProvider) {
            if (this.state.signout) {
                this.deleteCookie(AUTH_PROVIDER);
            } else if (this.state.redirectUri && this.props.history.action !== 'POP') {
                // do not auto redirect on browser Back event (history POP)
                this.authenticate(this.state.lastAuthProvider);
            }
        }
    }

    componentWillReceiveProps(nextProps: LoginProps & RouteComponentProps<{}>) {
        this.setState(this._buildState(nextProps));
    }

    render() {
        const { redirectUri, lastAuthProvider, signout, isInIframe, allowedAuthOptions } = this.state;

        //relogin
        if (lastAuthProvider && !signout && redirectUri && this.props.history.action !== 'POP') {
            return <Spinner />;
        }

        const displayInColumn = isInIframe || allowedAuthOptions.length <= MAX_COUNT_TO_DISPLAY_LIKE_COLUMN || !allowedAuthOptions.includes(AuthProvider.email);

        return (
            <div className="App signin">
                <div className="body">
                    <div className="login-content">
                        <div className="logo-img"></div>
                        <div className="panel">
                            <div className="text">
                                Log in to PPM Express
                            </div>
                            <div className="row">
                                <div className="column panel-content">
                                    {allowedAuthOptions.includes(AuthProvider.email) && !isInIframe && <>
                                        <DefaultButton className='k-button email' onClick={() => this.authenticate(AuthProvider.email)} text="Log in with Email" />
                                        {allowedAuthOptions.filter(_ => _ !== AuthProvider.email).length > 1 && <div className='spliter'>
                                            <span>Or Log in with</span>
                                        </div>}
                                    </>}
                                    <div className={css("buttons", displayInColumn ? "column" : undefined)} >
                                        {allowedAuthOptions.includes(AuthProvider.o365) && this._renderAuthProviderLoginButton(AuthProvider.o365)}
                                        {allowedAuthOptions.includes(AuthProvider.govus_o365) && this._renderAuthProviderLoginButton(AuthProvider.govus_o365)}
                                        {allowedAuthOptions.includes(AuthProvider.google) && !isInIframe && this._renderAuthProviderLoginButton(AuthProvider.google)}
                                        {allowedAuthOptions.includes(AuthProvider.okta) && !isInIframe && this._renderAuthProviderLoginButton(AuthProvider.okta)}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="footer">
                    <div className="footer-img"></div>
                </div>
            </div>
        );
    }

    private _renderAuthProviderLoginButton(authProvider: AuthProvider) {
        return <DefaultButton
            className={css(`k-button ${AuthProvidersMap[authProvider].buttonClassName}`)}
            title={AuthProvidersMap[authProvider].title}
            onClick={() => this.authenticate(authProvider)}>
            <span className={AuthProvidersMap[authProvider].logoClassName} /> {AuthProvidersMap[authProvider].friendlyName}
        </DefaultButton>
    }

    private authenticate = (authProvider: AuthProvider) => {
        this._trackLogin(authProvider);

        const authSchema = authProvider === AuthProvider.okta
            ? this.props.security?.okta?.authSchemeName
            : authProvider;

        const url = `${this._getRegionPrefix()}/account/signin/${authSchema}`;
        const days_in_year = 365;
        setCookie(AUTH_PROVIDER, authProvider, days_in_year);

        if (this.state.isInIframe) {
            microsoftTeams.app.initialize().then(() => {
                microsoftTeams.authentication.authenticate({
                    url: this.addParam(url, "redirectUri", "/postLogin"),
                    width: 600,
                    height: 535
                })
                .finally(() => window.location.href = this.state.redirectUri || "");
            });
        }
        else {
            window.location.href = this.addParam(url, REDIRECTURI, this.state.redirectUri);
        }
    }
    private addParam = (url: string, name: string, value: any) => {
        if (!value) {
            return url;
        }
        return url + (url.indexOf('?') !== -1 ? '&' : '?') + name + '=' + encodeURIComponent(value);
    }
    private getCookie = (name: string) => {
        const v = document.cookie.match('(^|;) ?' + name + '=([^;]*)(;|$)');
        const cookie_value_idx = 2;
        return v ? v[cookie_value_idx] : null;
    }
    private deleteCookie = (name: string) => {
        document.cookie = name + '=; Path=/; Expires=Thu, 01 Jan 1970 00:00:01 GMT;';
    }
    private _getRegionPrefix = () => {
        return this.props.region ? `/${this.props.region}` : ``;
    }
    private _trackLogin = (authType: string) => {
        analytics.track('AUTH', { authType });
    }
}

function setCookie(name: string, value: string, days: number) {
    const d = new Date;
    const msec_in_day = 86400000;
    d.setTime(d.getTime() + msec_in_day * days);
    document.cookie = name + "=" + value + ";path=/;expires=" + d.toUTCString();
}

function moveToCookie(keys: string[], search: string) {
    const urlParams = new URLSearchParams(search);
    keys.forEach(key => {
        const value = urlParams.get(key);
        const five_min_part_of_day = 0.08;
        value && setCookie(key, value, five_min_part_of_day);
    })
}

export default withRouter<LoginProps>(LoginPage)